import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { TabSkeleton } from '@/components/panel/panels';
import { ErrorState } from '@/components/shell/ErrorState';
import { useAuth } from '@/lib/auth/auth-context';

import { AnsweredView } from './AnsweredView';
import { FixReview } from './FixReview';
import { queueQueryOptions, requestQueryOptions } from './food-requests-query';
import { NewFoodReview } from './NewFoodReview';

export interface FoodRequestScreenProps {
  readonly requestId: string;
  /** Another request's page, or the queue when there is none: navigating is the route's job. */
  readonly onOpen: (id: string | null) => void;
  /** A name request reviews in the queue's drawer. */
  readonly onOpenName: (id: string) => void;
}

/**
 * One request on its own page: a new food or a fix to review while it is
 * open, and once answered, what its answer wrote with Revert.
 */
export function FoodRequestScreen({ requestId, onOpen, onOpenName }: FoodRequestScreenProps) {
  const { api } = useAuth();
  const detail = useQuery(requestQueryOptions(api, requestId));
  const request = detail.data;
  const queue = useQuery({
    ...queueQueryOptions(api, { status: 'open', kind: request?.kind }),
    enabled: request !== undefined,
  });

  useEffect(() => {
    if (request?.kind === 'name') onOpenName(request.id);
  }, [request?.kind, request?.id, onOpenName]);

  if (detail.isError) {
    return (
      <ErrorState
        title="Could not load this request"
        message={detail.error.message}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }
  if (request === undefined || request.kind === 'name') return <TabSkeleton rows={5} />;

  const open = (queue.data ?? []).filter((other) => other.draftStatus !== 'queued');
  const at = open.findIndex((other) => other.id === request.id);
  const position = at === -1 ? null : `${String(at + 1)} of ${String(open.length)}`;
  const next =
    open.find((other, index) => index > at && other.id !== request.id) ??
    open.find((other) => other.id !== request.id);
  const onSkip = () => {
    onOpen(next?.id ?? null);
  };
  const onAnswered = () => {
    void detail.refetch();
    window.scrollTo({ top: 0 });
  };

  if (request.status !== 'open') {
    return (
      <AnsweredView
        key={request.id}
        request={request}
        onReopened={() => {
          void detail.refetch();
        }}
      />
    );
  }
  return request.kind === 'fix' ? (
    <FixReview
      key={request.id}
      request={request}
      position={position}
      onSkip={onSkip}
      onAnswered={onAnswered}
    />
  ) : (
    <NewFoodReview
      key={request.id}
      request={request}
      position={position}
      onSkip={onSkip}
      onAnswered={onAnswered}
    />
  );
}
